/**
 * TenantSwitcher — the workspace switcher in the top bar's user menu
 * (USR-F1-T6).
 *
 * A person who belongs to more than one tenant used to change tenant only by
 * signing out and in again: the old switcher component called two routes
 * the api never mounted, and nothing rendered it. This one reads the
 * server's answer and nothing else:
 *
 *   GET  /auth/contexts       which tenants the person may enter NOW (the live
 *                             memberships, read from the store) and which one
 *                             the bearer names — fetched once per shell load
 *   POST /auth/switch-tenant  rotate this session into the picked tenant
 *
 * On a 200 both tokens are stored through `defaultTokenStorage` and the page
 * is FULLY reloaded to '/' — the same landing the login page takes after
 * select-context — so the query cache, the decoded org in auth-context and
 * every per-tenant screen are rebuilt from the new bearer. On a refusal the
 * server's message is shown and the old tokens are kept.
 *
 * Hidden while loading, on error, for a one-tenant person and in the sysadmin
 * context (the platform surface has its own TOTP-gated door at sign-in). No
 * role or authority is derived here: the list is the server's, and every
 * request after the switch is authorised by the server inside the new tenant.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { apiClient, defaultTokenStorage } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';

/** One tenant as GET /auth/contexts answers it (the login picker's shape). */
export interface TenantContextOption {
  id: string;
  name: string;
  slug: string;
}

/** GET /auth/contexts. */
export interface TenantContextsResponse {
  currentTenantId: string | null;
  tenants: TenantContextOption[];
}

/** What the menu shows, or null when it shows nothing. */
export interface TenantSwitcherView {
  current: TenantContextOption | null;
  others: TenantContextOption[];
}

/**
 * The switcher's visibility rule, pure: nothing in the sysadmin context,
 * nothing until the server has answered, nothing for a person with one tenant.
 */
export function tenantSwitcherView(
  contextKind: 'tenant' | 'sysadmin',
  data: TenantContextsResponse | undefined,
): TenantSwitcherView | null {
  if (contextKind !== 'tenant' || !data) return null;
  if (data.tenants.length <= 1) return null;
  const current = data.tenants.find((t) => t.id === data.currentTenantId) ?? null;
  return { current, others: data.tenants.filter((t) => t.id !== data.currentTenantId) };
}

/** POST /auth/switch-tenant's 200. */
interface SwitchTenantResponse {
  accessToken: string;
  refreshToken: string;
}

export interface SwitchTenantDeps {
  /**
   * Make the bearer fresh BEFORE the refresh token is read. A near-expiry
   * bearer is refreshed by rotating the refresh token, so a token read first
   * would already be deleted when the switch lands (USR-F1-T6-R1).
   */
  freshen: () => Promise<unknown>;
  post: (endpoint: string, body: unknown) => Promise<SwitchTenantResponse>;
  storage: Pick<typeof defaultTokenStorage, 'getRefreshToken' | 'setTokens'>;
  reload: (path: string) => void;
  notifyError: (message: string) => void;
}

export function switchTenantDeps(
  client: Pick<typeof apiClient, 'post' | 'ensureFreshAccessToken'>,
  storage: SwitchTenantDeps['storage'],
  ui: Pick<SwitchTenantDeps, 'reload' | 'notifyError'>,
): SwitchTenantDeps {
  return {
    freshen: () => client.ensureFreshAccessToken(),
    post: (endpoint, body) => client.post<SwitchTenantResponse>(endpoint, body),
    storage,
    ...ui,
  };
}

const browserDeps: SwitchTenantDeps = switchTenantDeps(apiClient, defaultTokenStorage, {
  reload: (path) => window.location.assign(path),
  notifyError: (message) => toast.error(message),
});

/**
 * Switch: post the session's refresh token with the target tenant; on 200
 * store both tokens and reload the whole shell; on a refusal show the
 * server's message and leave the tokens alone. Answers whether it switched.
 */
export async function switchTenant(
  tenantId: string,
  deps: SwitchTenantDeps = browserDeps,
): Promise<boolean> {
  let res: SwitchTenantResponse | null = null;
  // At most two attempts. The second runs only when the first was refused 401
  // AND storage now holds a different refresh token than the one sent: the
  // token was rotated under us (another tab, or apiClient's own 401 refresh),
  // so the refusal was about a stale body, not about this person.
  for (let attempt = 0; attempt < 2 && !res; attempt++) {
    await deps.freshen();
    const refreshToken = deps.storage.getRefreshToken();
    if (!refreshToken) {
      deps.notifyError('Your session has ended. Please sign in again.');
      return false;
    }
    try {
      res = await deps.post('/auth/switch-tenant', { tenantId, refreshToken });
    } catch (err) {
      const status = (err as { status?: unknown } | null)?.status;
      const rotatedUnderUs =
        status === 401 && deps.storage.getRefreshToken() !== refreshToken;
      if (attempt === 0 && rotatedUnderUs) continue;
      deps.notifyError(
        err instanceof Error && err.message ? err.message : 'Could not switch workspace.',
      );
      return false;
    }
  }
  if (!res) return false;
  deps.storage.setTokens(res.accessToken, res.refreshToken);
  deps.reload('/');
  return true;
}

/**
 * The menu section, rendered inside the top bar's user DropdownMenu between
 * the name/email block and Settings.
 */
export function TenantSwitcher() {
  const { contextKind } = useAuth();
  const [pending, setPending] = useState(false);
  const { data } = useQuery<TenantContextsResponse>({
    queryKey: ['auth', 'contexts'],
    queryFn: () => apiClient.get<TenantContextsResponse>('/auth/contexts'),
    enabled: contextKind === 'tenant',
    staleTime: Infinity,
    retry: false,
  });

  const view = tenantSwitcherView(contextKind, data);
  if (!view) return null;

  const pick = async (tenantId: string) => {
    setPending(true);
    const switched = await switchTenant(tenantId);
    if (!switched) setPending(false);
  };

  return (
    <>
      <DropdownMenuSeparator />
      {view.current && (
        <div className="px-2 py-1.5">
          <p className="text-xs text-muted-foreground">Workspace</p>
          <p className="flex items-center gap-1.5 text-sm font-medium" title={view.current.name}>
            <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            <span className="truncate">{view.current.name}</span>
          </p>
        </div>
      )}
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Switch workspace
      </DropdownMenuLabel>
      {view.others.map((t) => (
        <DropdownMenuItem
          key={t.id}
          disabled={pending}
          title={`Switch to ${t.name}`}
          onSelect={() => {
            void pick(t.id);
          }}
        >
          <Building2 className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{t.name}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}
